import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const LocationStep = ({ formData, onChange, errors }) => {
  const [showMap, setShowMap] = useState(false);

  const philippineCities = [
    { value: 'manila', label: 'Manila' },
    { value: 'quezon-city', label: 'Quezon City' },
    { value: 'caloocan', label: 'Caloocan' },
    { value: 'davao', label: 'Davao City' },
    { value: 'cebu', label: 'Cebu City' },
    { value: 'zamboanga', label: 'Zamboanga City' },
    { value: 'taguig', label: 'Taguig' },
    { value: 'antipolo', label: 'Antipolo' },
    { value: 'pasig', label: 'Pasig' },
    { value: 'cagayan-de-oro', label: 'Cagayan de Oro' },
    { value: 'paranaque', label: 'Parañaque' },
    { value: 'las-pinas', label: 'Las Piñas' },
    { value: 'makati', label: 'Makati' },
    { value: 'muntinlupa', label: 'Muntinlupa' },
    { value: 'bacoor', label: 'Bacoor' },
  ];

  const radiusOptions = [
    { value: '1', label: '1 km' },
    { value: '5', label: '5 km' },
    { value: '10', label: '10 km' },
    { value: '25', label: '25 km' },
    { value: '50', label: '50 km' },
  ];

  const workLocationTypes = [
    { value: 'on-site', label: 'On-Site (Worker comes to location)' },
    { value: 'remote', label: 'Remote (Online/Virtual)' },
    { value: 'flexible', label: 'Flexible (Can be either)' },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-primary/10 border border-primary/20 rounded-md p-4 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="MapPin" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">Location Matters</h4>
            <p className="text-sm text-muted-foreground">
              Workers within your selected radius will see your job first. Accurate location helps match you with nearby qualified workers.
            </p>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Work Location Type <span className="text-error">*</span>
        </label>
        <div className="space-y-2">
          {workLocationTypes?.map((type) => (
            <label
              key={type?.value}
              className={`
                flex items-center gap-3 p-4 rounded-md border transition-smooth cursor-pointer
                ${formData?.workLocationType === type?.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }
              `}
            >
              <input
                type="radio"
                name="workLocationType"
                value={type?.value}
                checked={formData?.workLocationType === type?.value}
                onChange={(e) => onChange('workLocationType', e?.target?.value)}
                className="w-4 h-4 text-primary"
              />
              <span className="text-sm font-medium text-foreground">{type?.label}</span>
            </label>
          ))}
        </div>
        {errors?.workLocationType && (
          <p className="text-sm text-error mt-2">{errors?.workLocationType}</p>
        )}
      </div>
      {formData?.workLocationType !== 'remote' && (
        <>
          <Select
            label="City"
            placeholder="Select your city"
            options={philippineCities}
            value={formData?.city || ''}
            onChange={(value) => onChange('city', value)}
            error={errors?.city}
            required
            searchable
          />

          <Input
            label="Barangay/Street Address"
            type="text"
            placeholder="e.g., Barangay San Antonio, 123 Rizal Street"
            value={formData?.address || ''}
            onChange={(e) => onChange('address', e?.target?.value)}
            error={errors?.address}
            required
            description="Specific location where work will be performed"
          />

          <Select
            label="Search Radius"
            placeholder="How far can workers be?"
            options={radiusOptions}
            value={formData?.radius || '10'}
            onChange={(value) => onChange('radius', value)}
            description="Workers within this distance will see your job"
          />

          <div className="border border-border rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => setShowMap(!showMap)}
              className="w-full flex items-center justify-between p-4 bg-muted hover:bg-muted/80 transition-smooth"
            >
              <div className="flex items-center gap-3">
                <Icon name="Map" size={20} color="var(--color-primary)" />
                <span className="font-medium text-foreground">
                  {showMap ? 'Hide Map Preview' : 'Show Map Preview'}
                </span>
              </div>
              <Icon 
                name={showMap ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
              />
            </button>
            {showMap && (
              <div className="h-64 md:h-80 bg-muted">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Job Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=14.5995,120.9842&z=14&output=embed"
                  className="border-0"
                />
              </div>
            )}
          </div>
        </>
      )}
      <Input
        label="Landmark (Optional)"
        type="text"
        placeholder="e.g., Near SM Mall, Beside Jollibee"
        value={formData?.landmark || ''}
        onChange={(e) => onChange('landmark', e?.target?.value)}
        description="Help workers find your location easily"
      />
    </div>
  );
};

export default LocationStep;